// ISO 3166-1 country reference data — the COMPLETE set of 249 officially-assigned
// entries, sovereign states and dependent territories alike: this maps a verification
// document's issuing state / nationality to a country, and territories (Hong Kong,
// Gibraltar, Jersey, Greenland…) issue their own passports. `countries.test.ts` pins
// the set against the standard.
//
// Every entry carries both codes. Identity documents report alpha-3, so country data is
// keyed on `alpha3`; `code` (alpha-2) is what flags, dial codes and third-party inputs
// speak. `name` is the untranslated English short name — render `useTrCountry` from the
// `countries` namespace, keyed by alpha-3. The product derives a user's country from a
// verification document and deliberately ships NO country picker.

const COUNTRY_TABLE = [
    { name: 'Afghanistan', code: 'AF', alpha3: 'AFG' },
    { name: 'Åland Islands', code: 'AX', alpha3: 'ALA' },
    { name: 'Albania', code: 'AL', alpha3: 'ALB' },
    { name: 'Algeria', code: 'DZ', alpha3: 'DZA' },
    { name: 'American Samoa', code: 'AS', alpha3: 'ASM' },
    { name: 'Andorra', code: 'AD', alpha3: 'AND' },
    { name: 'Angola', code: 'AO', alpha3: 'AGO' },
    { name: 'Anguilla', code: 'AI', alpha3: 'AIA' },
    { name: 'Antarctica', code: 'AQ', alpha3: 'ATA' },
    { name: 'Antigua and Barbuda', code: 'AG', alpha3: 'ATG' },
    { name: 'Argentina', code: 'AR', alpha3: 'ARG' },
    { name: 'Armenia', code: 'AM', alpha3: 'ARM' },
    { name: 'Aruba', code: 'AW', alpha3: 'ABW' },
    { name: 'Australia', code: 'AU', alpha3: 'AUS' },
    { name: 'Austria', code: 'AT', alpha3: 'AUT' },
    { name: 'Azerbaijan', code: 'AZ', alpha3: 'AZE' },
    { name: 'Bahamas', code: 'BS', alpha3: 'BHS' },
    { name: 'Bahrain', code: 'BH', alpha3: 'BHR' },
    { name: 'Bangladesh', code: 'BD', alpha3: 'BGD' },
    { name: 'Barbados', code: 'BB', alpha3: 'BRB' },
    { name: 'Belarus', code: 'BY', alpha3: 'BLR' },
    { name: 'Belgium', code: 'BE', alpha3: 'BEL' },
    { name: 'Belize', code: 'BZ', alpha3: 'BLZ' },
    { name: 'Benin', code: 'BJ', alpha3: 'BEN' },
    { name: 'Bermuda', code: 'BM', alpha3: 'BMU' },
    { name: 'Bhutan', code: 'BT', alpha3: 'BTN' },
    { name: 'Bolivia', code: 'BO', alpha3: 'BOL' },
    { name: 'Bonaire, Sint Eustatius and Saba', code: 'BQ', alpha3: 'BES' },
    { name: 'Bosnia and Herzegovina', code: 'BA', alpha3: 'BIH' },
    { name: 'Botswana', code: 'BW', alpha3: 'BWA' },
    { name: 'Bouvet Island', code: 'BV', alpha3: 'BVT' },
    { name: 'Brazil', code: 'BR', alpha3: 'BRA' },
    { name: 'British Indian Ocean Territory', code: 'IO', alpha3: 'IOT' },
    { name: 'British Virgin Islands', code: 'VG', alpha3: 'VGB' },
    { name: 'Brunei', code: 'BN', alpha3: 'BRN' },
    { name: 'Bulgaria', code: 'BG', alpha3: 'BGR' },
    { name: 'Burkina Faso', code: 'BF', alpha3: 'BFA' },
    { name: 'Burundi', code: 'BI', alpha3: 'BDI' },
    { name: 'Cabo Verde', code: 'CV', alpha3: 'CPV' },
    { name: 'Cambodia', code: 'KH', alpha3: 'KHM' },
    { name: 'Cameroon', code: 'CM', alpha3: 'CMR' },
    { name: 'Canada', code: 'CA', alpha3: 'CAN' },
    { name: 'Cayman Islands', code: 'KY', alpha3: 'CYM' },
    { name: 'Central African Republic', code: 'CF', alpha3: 'CAF' },
    { name: 'Chad', code: 'TD', alpha3: 'TCD' },
    { name: 'Chile', code: 'CL', alpha3: 'CHL' },
    { name: 'China', code: 'CN', alpha3: 'CHN' },
    { name: 'Christmas Island', code: 'CX', alpha3: 'CXR' },
    { name: 'Cocos (Keeling) Islands', code: 'CC', alpha3: 'CCK' },
    { name: 'Colombia', code: 'CO', alpha3: 'COL' },
    { name: 'Comoros', code: 'KM', alpha3: 'COM' },
    { name: 'Congo', code: 'CG', alpha3: 'COG' },
    { name: 'Cook Islands', code: 'CK', alpha3: 'COK' },
    { name: 'Costa Rica', code: 'CR', alpha3: 'CRI' },
    { name: "Côte d'Ivoire", code: 'CI', alpha3: 'CIV' },
    { name: 'Croatia', code: 'HR', alpha3: 'HRV' },
    { name: 'Cuba', code: 'CU', alpha3: 'CUB' },
    { name: 'Curaçao', code: 'CW', alpha3: 'CUW' },
    { name: 'Cyprus', code: 'CY', alpha3: 'CYP' },
    { name: 'Czechia', code: 'CZ', alpha3: 'CZE' },
    { name: 'Denmark', code: 'DK', alpha3: 'DNK' },
    { name: 'Djibouti', code: 'DJ', alpha3: 'DJI' },
    { name: 'Dominica', code: 'DM', alpha3: 'DMA' },
    { name: 'Dominican Republic', code: 'DO', alpha3: 'DOM' },
    { name: 'DR Congo', code: 'CD', alpha3: 'COD' },
    { name: 'Ecuador', code: 'EC', alpha3: 'ECU' },
    { name: 'Egypt', code: 'EG', alpha3: 'EGY' },
    { name: 'El Salvador', code: 'SV', alpha3: 'SLV' },
    { name: 'Equatorial Guinea', code: 'GQ', alpha3: 'GNQ' },
    { name: 'Eritrea', code: 'ER', alpha3: 'ERI' },
    { name: 'Estonia', code: 'EE', alpha3: 'EST' },
    { name: 'Eswatini', code: 'SZ', alpha3: 'SWZ' },
    { name: 'Ethiopia', code: 'ET', alpha3: 'ETH' },
    { name: 'Falkland Islands', code: 'FK', alpha3: 'FLK' },
    { name: 'Faroe Islands', code: 'FO', alpha3: 'FRO' },
    { name: 'Fiji', code: 'FJ', alpha3: 'FJI' },
    { name: 'Finland', code: 'FI', alpha3: 'FIN' },
    { name: 'France', code: 'FR', alpha3: 'FRA' },
    { name: 'French Guiana', code: 'GF', alpha3: 'GUF' },
    { name: 'French Polynesia', code: 'PF', alpha3: 'PYF' },
    { name: 'French Southern Territories', code: 'TF', alpha3: 'ATF' },
    { name: 'Gabon', code: 'GA', alpha3: 'GAB' },
    { name: 'Gambia', code: 'GM', alpha3: 'GMB' },
    { name: 'Georgia', code: 'GE', alpha3: 'GEO' },
    { name: 'Germany', code: 'DE', alpha3: 'DEU' },
    { name: 'Ghana', code: 'GH', alpha3: 'GHA' },
    { name: 'Gibraltar', code: 'GI', alpha3: 'GIB' },
    { name: 'Greece', code: 'GR', alpha3: 'GRC' },
    { name: 'Greenland', code: 'GL', alpha3: 'GRL' },
    { name: 'Grenada', code: 'GD', alpha3: 'GRD' },
    { name: 'Guadeloupe', code: 'GP', alpha3: 'GLP' },
    { name: 'Guam', code: 'GU', alpha3: 'GUM' },
    { name: 'Guatemala', code: 'GT', alpha3: 'GTM' },
    { name: 'Guernsey', code: 'GG', alpha3: 'GGY' },
    { name: 'Guinea', code: 'GN', alpha3: 'GIN' },
    { name: 'Guinea-Bissau', code: 'GW', alpha3: 'GNB' },
    { name: 'Guyana', code: 'GY', alpha3: 'GUY' },
    { name: 'Haiti', code: 'HT', alpha3: 'HTI' },
    { name: 'Heard Island and McDonald Islands', code: 'HM', alpha3: 'HMD' },
    { name: 'Honduras', code: 'HN', alpha3: 'HND' },
    { name: 'Hong Kong', code: 'HK', alpha3: 'HKG' },
    { name: 'Hungary', code: 'HU', alpha3: 'HUN' },
    { name: 'Iceland', code: 'IS', alpha3: 'ISL' },
    { name: 'India', code: 'IN', alpha3: 'IND' },
    { name: 'Indonesia', code: 'ID', alpha3: 'IDN' },
    { name: 'Iran', code: 'IR', alpha3: 'IRN' },
    { name: 'Iraq', code: 'IQ', alpha3: 'IRQ' },
    { name: 'Ireland', code: 'IE', alpha3: 'IRL' },
    { name: 'Isle of Man', code: 'IM', alpha3: 'IMN' },
    { name: 'Israel', code: 'IL', alpha3: 'ISR' },
    { name: 'Italy', code: 'IT', alpha3: 'ITA' },
    { name: 'Jamaica', code: 'JM', alpha3: 'JAM' },
    { name: 'Japan', code: 'JP', alpha3: 'JPN' },
    { name: 'Jersey', code: 'JE', alpha3: 'JEY' },
    { name: 'Jordan', code: 'JO', alpha3: 'JOR' },
    { name: 'Kazakhstan', code: 'KZ', alpha3: 'KAZ' },
    { name: 'Kenya', code: 'KE', alpha3: 'KEN' },
    { name: 'Kiribati', code: 'KI', alpha3: 'KIR' },
    { name: 'Kuwait', code: 'KW', alpha3: 'KWT' },
    { name: 'Kyrgyzstan', code: 'KG', alpha3: 'KGZ' },
    { name: 'Laos', code: 'LA', alpha3: 'LAO' },
    { name: 'Latvia', code: 'LV', alpha3: 'LVA' },
    { name: 'Lebanon', code: 'LB', alpha3: 'LBN' },
    { name: 'Lesotho', code: 'LS', alpha3: 'LSO' },
    { name: 'Liberia', code: 'LR', alpha3: 'LBR' },
    { name: 'Libya', code: 'LY', alpha3: 'LBY' },
    { name: 'Liechtenstein', code: 'LI', alpha3: 'LIE' },
    { name: 'Lithuania', code: 'LT', alpha3: 'LTU' },
    { name: 'Luxembourg', code: 'LU', alpha3: 'LUX' },
    { name: 'Macao', code: 'MO', alpha3: 'MAC' },
    { name: 'Madagascar', code: 'MG', alpha3: 'MDG' },
    { name: 'Malawi', code: 'MW', alpha3: 'MWI' },
    { name: 'Malaysia', code: 'MY', alpha3: 'MYS' },
    { name: 'Maldives', code: 'MV', alpha3: 'MDV' },
    { name: 'Mali', code: 'ML', alpha3: 'MLI' },
    { name: 'Malta', code: 'MT', alpha3: 'MLT' },
    { name: 'Marshall Islands', code: 'MH', alpha3: 'MHL' },
    { name: 'Martinique', code: 'MQ', alpha3: 'MTQ' },
    { name: 'Mauritania', code: 'MR', alpha3: 'MRT' },
    { name: 'Mauritius', code: 'MU', alpha3: 'MUS' },
    { name: 'Mayotte', code: 'YT', alpha3: 'MYT' },
    { name: 'Mexico', code: 'MX', alpha3: 'MEX' },
    { name: 'Micronesia', code: 'FM', alpha3: 'FSM' },
    { name: 'Moldova', code: 'MD', alpha3: 'MDA' },
    { name: 'Monaco', code: 'MC', alpha3: 'MCO' },
    { name: 'Mongolia', code: 'MN', alpha3: 'MNG' },
    { name: 'Montenegro', code: 'ME', alpha3: 'MNE' },
    { name: 'Montserrat', code: 'MS', alpha3: 'MSR' },
    { name: 'Morocco', code: 'MA', alpha3: 'MAR' },
    { name: 'Mozambique', code: 'MZ', alpha3: 'MOZ' },
    { name: 'Myanmar', code: 'MM', alpha3: 'MMR' },
    { name: 'Namibia', code: 'NA', alpha3: 'NAM' },
    { name: 'Nauru', code: 'NR', alpha3: 'NRU' },
    { name: 'Nepal', code: 'NP', alpha3: 'NPL' },
    { name: 'Netherlands', code: 'NL', alpha3: 'NLD' },
    { name: 'New Caledonia', code: 'NC', alpha3: 'NCL' },
    { name: 'New Zealand', code: 'NZ', alpha3: 'NZL' },
    { name: 'Nicaragua', code: 'NI', alpha3: 'NIC' },
    { name: 'Niger', code: 'NE', alpha3: 'NER' },
    { name: 'Nigeria', code: 'NG', alpha3: 'NGA' },
    { name: 'Niue', code: 'NU', alpha3: 'NIU' },
    { name: 'Norfolk Island', code: 'NF', alpha3: 'NFK' },
    { name: 'North Korea', code: 'KP', alpha3: 'PRK' },
    { name: 'North Macedonia', code: 'MK', alpha3: 'MKD' },
    { name: 'Northern Mariana Islands', code: 'MP', alpha3: 'MNP' },
    { name: 'Norway', code: 'NO', alpha3: 'NOR' },
    { name: 'Oman', code: 'OM', alpha3: 'OMN' },
    { name: 'Pakistan', code: 'PK', alpha3: 'PAK' },
    { name: 'Palau', code: 'PW', alpha3: 'PLW' },
    { name: 'Palestine', code: 'PS', alpha3: 'PSE' },
    { name: 'Panama', code: 'PA', alpha3: 'PAN' },
    { name: 'Papua New Guinea', code: 'PG', alpha3: 'PNG' },
    { name: 'Paraguay', code: 'PY', alpha3: 'PRY' },
    { name: 'Peru', code: 'PE', alpha3: 'PER' },
    { name: 'Philippines', code: 'PH', alpha3: 'PHL' },
    { name: 'Pitcairn Islands', code: 'PN', alpha3: 'PCN' },
    { name: 'Poland', code: 'PL', alpha3: 'POL' },
    { name: 'Portugal', code: 'PT', alpha3: 'PRT' },
    { name: 'Puerto Rico', code: 'PR', alpha3: 'PRI' },
    { name: 'Qatar', code: 'QA', alpha3: 'QAT' },
    { name: 'Réunion', code: 'RE', alpha3: 'REU' },
    { name: 'Romania', code: 'RO', alpha3: 'ROU' },
    { name: 'Russia', code: 'RU', alpha3: 'RUS' },
    { name: 'Rwanda', code: 'RW', alpha3: 'RWA' },
    { name: 'Saint Barthélemy', code: 'BL', alpha3: 'BLM' },
    { name: 'Saint Helena, Ascension and Tristan da Cunha', code: 'SH', alpha3: 'SHN' },
    { name: 'Saint Kitts and Nevis', code: 'KN', alpha3: 'KNA' },
    { name: 'Saint Lucia', code: 'LC', alpha3: 'LCA' },
    { name: 'Saint Martin', code: 'MF', alpha3: 'MAF' },
    { name: 'Saint Pierre and Miquelon', code: 'PM', alpha3: 'SPM' },
    { name: 'Saint Vincent and the Grenadines', code: 'VC', alpha3: 'VCT' },
    { name: 'Samoa', code: 'WS', alpha3: 'WSM' },
    { name: 'San Marino', code: 'SM', alpha3: 'SMR' },
    { name: 'Sao Tome and Principe', code: 'ST', alpha3: 'STP' },
    { name: 'Saudi Arabia', code: 'SA', alpha3: 'SAU' },
    { name: 'Senegal', code: 'SN', alpha3: 'SEN' },
    { name: 'Serbia', code: 'RS', alpha3: 'SRB' },
    { name: 'Seychelles', code: 'SC', alpha3: 'SYC' },
    { name: 'Sierra Leone', code: 'SL', alpha3: 'SLE' },
    { name: 'Singapore', code: 'SG', alpha3: 'SGP' },
    { name: 'Sint Maarten', code: 'SX', alpha3: 'SXM' },
    { name: 'Slovakia', code: 'SK', alpha3: 'SVK' },
    { name: 'Slovenia', code: 'SI', alpha3: 'SVN' },
    { name: 'Solomon Islands', code: 'SB', alpha3: 'SLB' },
    { name: 'Somalia', code: 'SO', alpha3: 'SOM' },
    { name: 'South Africa', code: 'ZA', alpha3: 'ZAF' },
    { name: 'South Georgia and the South Sandwich Islands', code: 'GS', alpha3: 'SGS' },
    { name: 'South Korea', code: 'KR', alpha3: 'KOR' },
    { name: 'South Sudan', code: 'SS', alpha3: 'SSD' },
    { name: 'Spain', code: 'ES', alpha3: 'ESP' },
    { name: 'Sri Lanka', code: 'LK', alpha3: 'LKA' },
    { name: 'Sudan', code: 'SD', alpha3: 'SDN' },
    { name: 'Suriname', code: 'SR', alpha3: 'SUR' },
    { name: 'Svalbard and Jan Mayen', code: 'SJ', alpha3: 'SJM' },
    { name: 'Sweden', code: 'SE', alpha3: 'SWE' },
    { name: 'Switzerland', code: 'CH', alpha3: 'CHE' },
    { name: 'Syria', code: 'SY', alpha3: 'SYR' },
    { name: 'Taiwan', code: 'TW', alpha3: 'TWN' },
    { name: 'Tajikistan', code: 'TJ', alpha3: 'TJK' },
    { name: 'Tanzania', code: 'TZ', alpha3: 'TZA' },
    { name: 'Thailand', code: 'TH', alpha3: 'THA' },
    { name: 'Timor-Leste', code: 'TL', alpha3: 'TLS' },
    { name: 'Togo', code: 'TG', alpha3: 'TGO' },
    { name: 'Tokelau', code: 'TK', alpha3: 'TKL' },
    { name: 'Tonga', code: 'TO', alpha3: 'TON' },
    { name: 'Trinidad and Tobago', code: 'TT', alpha3: 'TTO' },
    { name: 'Tunisia', code: 'TN', alpha3: 'TUN' },
    { name: 'Turkey', code: 'TR', alpha3: 'TUR' },
    { name: 'Turkmenistan', code: 'TM', alpha3: 'TKM' },
    { name: 'Turks and Caicos Islands', code: 'TC', alpha3: 'TCA' },
    { name: 'Tuvalu', code: 'TV', alpha3: 'TUV' },
    { name: 'U.S. Virgin Islands', code: 'VI', alpha3: 'VIR' },
    { name: 'Uganda', code: 'UG', alpha3: 'UGA' },
    { name: 'Ukraine', code: 'UA', alpha3: 'UKR' },
    { name: 'United Arab Emirates', code: 'AE', alpha3: 'ARE' },
    { name: 'United Kingdom', code: 'GB', alpha3: 'GBR' },
    { name: 'United States', code: 'US', alpha3: 'USA' },
    { name: 'United States Minor Outlying Islands', code: 'UM', alpha3: 'UMI' },
    { name: 'Uruguay', code: 'UY', alpha3: 'URY' },
    { name: 'Uzbekistan', code: 'UZ', alpha3: 'UZB' },
    { name: 'Vanuatu', code: 'VU', alpha3: 'VUT' },
    { name: 'Vatican City', code: 'VA', alpha3: 'VAT' },
    { name: 'Venezuela', code: 'VE', alpha3: 'VEN' },
    { name: 'Vietnam', code: 'VN', alpha3: 'VNM' },
    { name: 'Wallis and Futuna', code: 'WF', alpha3: 'WLF' },
    { name: 'Western Sahara', code: 'EH', alpha3: 'ESH' },
    { name: 'Yemen', code: 'YE', alpha3: 'YEM' },
    { name: 'Zambia', code: 'ZM', alpha3: 'ZMB' },
    { name: 'Zimbabwe', code: 'ZW', alpha3: 'ZWE' },
] as const;

/** ISO 3166-1 alpha-2 code (`'EG'`). */
export type CountryCode = (typeof COUNTRY_TABLE)[number]['code'];

/** ISO 3166-1 alpha-3 code (`'EGY'`) — what verification documents report. */
export type Alpha3CountryCode = (typeof COUNTRY_TABLE)[number]['alpha3'];

export interface Country {
    /** Untranslated English reference name. Render `useTrCountry(alpha3)` instead. */
    name: string;
    /** ISO 3166-1 alpha-2. */
    code: CountryCode;
    /** ISO 3166-1 alpha-3. */
    alpha3: Alpha3CountryCode;
}

export const COUNTRIES: readonly Country[] = COUNTRY_TABLE;

const BY_ALPHA2 = new Map<string, Country>(COUNTRIES.map((country) => [country.code, country]));
const BY_ALPHA3 = new Map<string, Country>(COUNTRIES.map((country) => [country.alpha3, country]));

/** Uppercases a candidate code; anything non-string becomes an unmatchable ''. */
const normalize = (value: unknown): string =>
    typeof value === 'string' ? value.trim().toUpperCase() : '';

/** The country for an alpha-2 code, case-insensitively. Lookups degrade, never throw. */
export const findCountryByAlpha2 = (code: unknown): Country | undefined =>
    BY_ALPHA2.get(normalize(code));

/** The country for an alpha-3 code, case-insensitively; `undefined` if unknown. */
export const findCountryByAlpha3 = (code: unknown): Country | undefined =>
    BY_ALPHA3.get(normalize(code));

/** `'EG'` → `'EGY'`; `undefined` for an unknown code. */
export const alpha2ToAlpha3 = (code: unknown): Alpha3CountryCode | undefined =>
    findCountryByAlpha2(code)?.alpha3;

/** `'EGY'` → `'EG'`; `undefined` for an unknown code. */
export const alpha3ToAlpha2 = (code: unknown): CountryCode | undefined =>
    findCountryByAlpha3(code)?.code;

// A type guard must be TRUE OF THE VALUE it narrowed, so these do NOT normalize:
// `isAlpha3CountryCode('hkg')` would type-check and then return undefined from every
// uppercase-keyed structure (the `countries:<ALPHA3>` catalog included). Untrusted input
// — a scanned document's issuing state — goes to the COERCERS below, which normalize and
// hand back the canonical code.

/** Type guard: is this EXACTLY a known alpha-2 code? Canonical uppercase only. */
export const isCountryCode = (value: unknown): value is CountryCode =>
    typeof value === 'string' && BY_ALPHA2.has(value);

/** Type guard: is this EXACTLY a known alpha-3 code? Canonical uppercase only. */
export const isAlpha3CountryCode = (value: unknown): value is Alpha3CountryCode =>
    typeof value === 'string' && BY_ALPHA3.has(value);

/**
 * Coerces untrusted input to a canonical alpha-2 code — `' eg '` → `'EG'`, `undefined`
 * when it names no country. Use this, not the guard, for values off the wire.
 */
export const asAlpha2 = (value: unknown): CountryCode | undefined =>
    findCountryByAlpha2(value)?.code;

/** Coerces untrusted input to a canonical alpha-3 code — `'egy'` → `'EGY'`. */
export const asAlpha3 = (value: unknown): Alpha3CountryCode | undefined =>
    findCountryByAlpha3(value)?.alpha3;
